import * as Sentry from '@sentry/react';

// Matches sensitive query/hash params — stops at & or # to preserve URL fragments
const SENSITIVE_PARAMS = /([?&#])(token|access_token|refresh_token|code|state|session|key|secret|password|pwd)=[^&#]*/gi;

function scrubUrl(url: string): string {
  return url.replace(SENSITIVE_PARAMS, (_, prefix: string, param: string) =>
    `${prefix}${param}=[Filtered]`,
  );
}

function scrubBreadcrumbUrls(breadcrumb: Sentry.Breadcrumb): void {
  if (!breadcrumb.data) return;
  for (const field of ['url', 'from', 'to'] as const) {
    const val = breadcrumb.data[field];
    if (typeof val === 'string') {
      breadcrumb.data[field] = scrubUrl(val);
    }
  }
}

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_ENV || 'development',
  release: import.meta.env.VITE_SENTRY_RELEASE,

  sendDefaultPii: false,
  tracesSampleRate: 0,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,

  beforeSend(event) {
    if (event.request?.url) {
      event.request.url = scrubUrl(event.request.url);
    }

    // Defense-in-depth: strip request body, auth headers, and query_string
    if (event.request) {
      delete event.request.cookies;
      delete event.request.data;
      if (typeof event.request.query_string === 'string') {
        event.request.query_string = scrubUrl('?' + event.request.query_string).slice(1);
      }
      if (event.request.headers) {
        delete event.request.headers['authorization'];
        delete event.request.headers['cookie'];
      }
    }

    if (event.breadcrumbs) {
      for (const breadcrumb of event.breadcrumbs) {
        scrubBreadcrumbUrls(breadcrumb);
      }
    }

    return event;
  },

  beforeBreadcrumb,
});

// Exported (named) for unit testing — Sentry.init above wires this in.
// Sentry serializes breadcrumbs into the event context sent to the dashboard;
// any string we don't drop here ends up persisted server-side.

/**
 * EXK-294 F5 generalization — detects dev/ops paths in breadcrumb messages.
 * Cobre:
 *   - POSIX: /Users/, /home/, /tmp/, /var/folders/, /private/tmp/
 *   - POSIX corp/managed: /opt/, /usr/local/, /Volumes/ (NAS, drives macOS)
 *   - Windows: C:\Users\ ou C:/Users/ (case-insensitive drive letter)
 *
 * Pure function pra testar em isolation. Export pra unit test sem mockar
 * Sentry init.
 */
export function containsDevPath(msg: string): boolean {
  // Windows: drive letter (C:\... ou C:/...)
  if (/[A-Z]:[\\/][\w.\-\\/]+/i.test(msg)) return true;
  // POSIX classes (Exekuity dev primary)
  if (/\/(?:Users|home|tmp|var\/folders|private\/tmp|opt|usr\/local|Volumes)\/[\w.\-/]+/i.test(msg)) {
    return true;
  }
  return false;
}

export function beforeBreadcrumb(
  breadcrumb: Sentry.Breadcrumb,
): Sentry.Breadcrumb | null {
  if (breadcrumb.category !== 'console') return breadcrumb;
  const msg = typeof breadcrumb.message === 'string' ? breadcrumb.message : '';
  if (/localStorage|sessionStorage|token|password|secret|authorization/i.test(msg)) {
    return null;
  }
  // EXK-235 F2.2 + EXK-294 F5 (Windows/extra dirs) — drop runbook markers
  // that leak internal ops paths/CLI. Jira IDs kept intentionally (triage).
  if (/Re-seed via|REPLACE=1|seed-estudio-scenarios/i.test(msg) || containsDevPath(msg)) {
    return null;
  }
  return breadcrumb;
}
